import { tr, usePartnerTranslation } from "../../lib/language";
import React, { useRef, useState } from "react";
import axios from "axios";
import { Loader2, Mail, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { parseApiError } from "../../lib/api";
import { cn } from "../../lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Step-up OTP gate.
 *
 * Flow:
 *   1. Modal opens, showing the target email + a "Send OTP" button (NO auto-send).
 *   2. User clicks Send → a fresh 6-digit code is dispatched.
 *   3. User types the code and confirms.
 *   4. onVerified(code) fires so the parent can open the real edit modal.
 *
 * The backend always re-verifies the code on the subsequent PUT.
 */
const OtpGateModal = ({
  open,
  onClose,
  email,
  title = "Verify it's you",
  description = "For your security, we'll send a 6-digit code to your email before editing this section.",
  onVerified,
}) => {
  usePartnerTranslation();
  const [otp, setOtp] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [lastSentAt, setLastSentAt] = useState(null);
  const timerRef = useRef(null);
  const [, forceTick] = useState(0); // drive the countdown re-render

  // Reset state whenever the modal opens
  React.useEffect(() => {
    if (open) {
      setOtp("");
      setError("");
      setLastSentAt(null);
    }
  }, [open]);

  // 1-second tick while a cooldown is active
  React.useEffect(() => {
    if (!open || !lastSentAt) return undefined;
    timerRef.current = setInterval(() => forceTick((t) => t + 1), 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [open, lastSentAt]);

  const sendCode = async () => {
    if (!email) return;
    setSending(true);
    setError("");
    try {
      const res = await axios.post(`${API}/finance/me/payment-method/otp`, { email });
      setLastSentAt(Date.now());
      const devOtp = res.data?.dev_otp;
      toast.success(tr("Code sent"), {
        description: devOtp
          ? tr("DEV OTP: {{value0}} — expires in 10 minutes. (No mailer wired yet)", { value0: devOtp })
          : tr("We emailed a 6-digit code to {{value0}}.", { value0: email }),
      });
    } catch (err) {
      const { message } = parseApiError(err, tr("Couldn't send the code. Try again."));
      setError(message);
    } finally {
      setSending(false);
    }
  };

  const secondsSinceSend = lastSentAt
    ? Math.floor((Date.now() - lastSentAt) / 1000)
    : 999;
  const canResend = secondsSinceSend >= 30;
  const sentOnce = !!lastSentAt;

  const submit = async () => {
    if (otp.length !== 6) return;
    setVerifying(true);
    setError("");
    try {
      await axios.post(`${API}/finance/me/payment-method/verify-otp`, { email, otp });
      onVerified(otp);
    } catch (err) {
      const { message } = parseApiError(err, tr("That code didn't match. Please try again."));
      setError(message);
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="otp-gate-modal"
        className="max-w-[460px] rounded-2xl border-[#EFE4E4] p-0"
      >
        <DialogHeader className="border-b border-[#EFE4E4] px-6 py-5">
          <DialogTitle className="text-lg font-extrabold tracking-tight text-[#0F1115]">
            {title}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#6B7280]">
            {description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 px-6 py-6">
          <div className="flex items-center gap-2 rounded-lg bg-[#FFF2F2] px-3 py-2 text-sm">
            <Mail className="h-4 w-4 flex-shrink-0 text-[#FF5757]" strokeWidth={1.8} />
            <span
              data-testid="otp-gate-email"
              className="truncate font-semibold text-[#0F1115]"
            >
              {email}
            </span>
            {sentOnce && (
              <span
                data-testid="otp-gate-sent-indicator"
                className="ms-auto inline-flex items-center gap-1 text-xs text-[#0F6B2B]"
              >
                <CheckCircle2 className="h-3.5 w-3.5" />{tr(" Code sent")}</span>
            )}
          </div>

          {!sentOnce ? (
            /* ---------- Step 1: ask the user to send the code ---------- */
            <div data-testid="otp-gate-send-step" className="space-y-3">
              <button
                type="button"
                data-testid="otp-gate-send-btn"
                onClick={sendCode}
                disabled={sending || !email}
                className="humrahe-btn-primary inline-flex h-11 w-full items-center justify-center gap-2 rounded-md text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50"
              >
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />{tr("Sending…")}</>
                ) : (
                  <>
                    <Send className="h-4 w-4" strokeWidth={2} />{tr("Send OTP to this email")}</>
                )}
              </button>
              {error && (
                <p
                  data-testid="otp-gate-send-error"
                  className="rounded-md bg-[#FFE9E9] px-3 py-2 text-sm text-[#9B0000]"
                >
                  {error}
                </p>
              )}
            </div>
          ) : (
            /* ---------- Step 2: enter the 6-digit code ---------- */
            <div data-testid="otp-gate-verify-step">
              <label
                htmlFor="otp-gate-input"
                className="text-sm font-semibold text-[#0F1115]"
              >{tr("6-digit code")}</label>
              <input
                id="otp-gate-input"
                data-testid="otp-gate-input"
                type="text"
                inputMode="numeric"
                autoFocus
                value={otp}
                maxLength={6}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setOtp(v);
                  setError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && otp.length === 6) submit();
                }}
                placeholder="••••••"
                className={cn(
                  "humrahe-input mt-2 tracking-[0.45em] text-center text-lg font-semibold",
                  error &&
                    "border-[#9B0000] focus:border-[#9B0000] focus:shadow-[0_0_0_4px_rgba(155,0,0,0.12)]",
                )}
              />
              {error && (
                <p
                  data-testid="otp-gate-error"
                  className="mt-2 rounded-md bg-[#FFE9E9] px-3 py-2 text-sm text-[#9B0000]"
                >
                  {error}
                </p>
              )}
              <div className="mt-3 flex items-center justify-between text-xs">
                <span className="text-[#6B7280]">{tr("Didn't get it?")}{" "}
                  <button
                    type="button"
                    data-testid="otp-gate-resend"
                    disabled={!canResend || sending}
                    onClick={sendCode}
                    className={cn(
                      "font-semibold text-[#2476FF] transition-opacity hover:underline",
                      (!canResend || sending) &&
                        "cursor-not-allowed opacity-50 no-underline",
                    )}
                  >
                    {canResend
                      ? tr("Resend code")
                      : tr("Resend in {{value0}}s", { value0: 30 - secondsSinceSend })}
                  </button>
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-[#EFE4E4] px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            data-testid="otp-gate-cancel"
            className="inline-flex h-10 items-center rounded-md border border-[#E6E8EC] px-4 text-sm font-semibold text-[#0F1115] transition-colors hover:border-[#0F1115]"
          >{tr("Cancel")}</button>
          {sentOnce && (
            <button
              type="button"
              data-testid="otp-gate-submit"
              onClick={submit}
              disabled={otp.length !== 6 || verifying}
              className="humrahe-btn-primary inline-flex h-10 items-center rounded-md px-5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50"
            >
              {verifying ? (
                <>
                  <Loader2 className="me-2 h-4 w-4 animate-spin" />{tr("Verifying…")}</>
              ) : (
                tr("Verify & continue")
              )}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OtpGateModal;
